import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Plus,
  Info,
  MessageSquare,
  AlertTriangle,
  Wrench,
  HelpCircle,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";

type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "BLOCKED";

const statusLabels: Record<TicketStatus, string> = {
  OPEN: "Abierto",
  IN_PROGRESS: "En progreso",
  RESOLVED: "Resuelto",
  BLOCKED: "Bloqueado",
};

const statusColors: Record<
  TicketStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  OPEN: "default",
  IN_PROGRESS: "secondary",
  RESOLVED: "outline",
  BLOCKED: "destructive",
};

const statusBorderColors: Record<TicketStatus, string> = {
  OPEN: "border-l-blue-500",
  IN_PROGRESS: "border-l-amber-500",
  RESOLVED: "border-l-green-500",
  BLOCKED: "border-l-red-500",
};

const statusIcons: Record<
  TicketStatus,
  React.ComponentType<{ className?: string }>
> = {
  OPEN: Clock,
  IN_PROGRESS: Loader2,
  RESOLVED: CheckCircle,
  BLOCKED: XCircle,
};

const categoryLabels = {
  GENERAL: "General",
  URGENT: "Urgente",
  MAINTENANCE: "Mantenimiento",
};

const categoryIcons = {
  GENERAL: HelpCircle,
  URGENT: AlertTriangle,
  MAINTENANCE: Wrench,
};

function formatRelativeTime(date: Date | string) {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Ahora";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return then.toLocaleDateString("es-AR", {
    month: "short",
    day: "numeric",
  });
}

export function TicketsPage() {
  const { selectedCommunityId } = useCommunity();
  const [activeTab, setActiveTab] = useState<"all" | TicketStatus>("all");

  const { data: communities, isLoading: communitiesLoading } =
    trpc.amenity.getUserCommunities.useQuery();

  const selectedCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );
  const isAdmin = selectedCommunity?.role === "COMMUNITY_ADMIN";

  const statusFilter = activeTab === "all" ? undefined : activeTab;

  const { data: tickets, isLoading: ticketsLoading } =
    trpc.ticket.list.useQuery(
      { communityId: selectedCommunityId || "", status: statusFilter },
      { enabled: !!selectedCommunityId },
    );

  const { data: stats } = trpc.ticket.getStats.useQuery(
    { communityId: selectedCommunityId || "" },
    { enabled: !!selectedCommunityId && isAdmin },
  );

  // No communities
  if (!communitiesLoading && (!communities || communities.length === 0)) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Tickets</h1>
          <p className="text-muted-foreground">Creá y seguí tus tickets.</p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No sos miembro de ningún barrio. Contactá al administrador para
            obtener acceso.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Tickets</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "Gestioná los tickets del barrio."
              : "Creá y seguí tus solicitudes."}
          </p>
        </div>

        <Button asChild>
          <Link to="/tickets/new">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo ticket
          </Link>
        </Button>
      </div>

      {/* Admin stats */}
      {isAdmin && stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.open}</div>
              <p className="text-xs text-muted-foreground">Abiertos</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.inProgress}</div>
              <p className="text-xs text-muted-foreground">En progreso</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.resolved}</div>
              <p className="text-xs text-muted-foreground">Resueltos</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.blocked}</div>
              <p className="text-xs text-muted-foreground">Bloqueados</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as "all" | TicketStatus)}
      >
        <TabsList className="mb-6">
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="OPEN">Abiertos</TabsTrigger>
          <TabsTrigger value="IN_PROGRESS">En progreso</TabsTrigger>
          <TabsTrigger value="RESOLVED">Resueltos</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {communitiesLoading || ticketsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tickets && tickets.items.length > 0 ? (
            <div className="space-y-4">
              {tickets.items.map((ticket) => {
                const CategoryIcon = categoryIcons[ticket.category];
                const StatusIcon = statusIcons[ticket.status];

                return (
                  <Link
                    key={ticket.id}
                    to="/tickets/$ticketId"
                    params={{ ticketId: ticket.id }}
                  >
                    <Card
                      className={`cursor-pointer border-l-4 transition-colors hover:bg-muted/50 ${statusBorderColors[ticket.status]}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                              <CategoryIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">
                                {ticket.title}
                              </CardTitle>
                              <CardDescription className="flex flex-wrap items-center gap-2">
                                <Badge
                                  variant={statusColors[ticket.status]}
                                  className="text-xs"
                                >
                                  <StatusIcon className="mr-1 h-3 w-3" />
                                  {statusLabels[ticket.status]}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {categoryLabels[ticket.category]}
                                </Badge>
                                {isAdmin && (
                                  <span className="text-xs text-muted-foreground">
                                    por {ticket.requester.name || "Sin nombre"}
                                  </span>
                                )}
                              </CardDescription>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 text-muted-foreground">
                            {ticket.commentCount > 0 && (
                              <div className="flex items-center gap-1 text-sm">
                                <MessageSquare className="h-4 w-4" />
                                {ticket.commentCount}
                              </div>
                            )}
                            <span className="text-sm">
                              {formatRelativeTime(ticket.createdAt)}
                            </span>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">
                  {activeTab === "all"
                    ? "No hay tickets todavía."
                    : `No hay tickets ${statusLabels[activeTab as TicketStatus].toLowerCase()}.`}
                </p>
                <Button variant="outline" className="mt-4" asChild>
                  <Link to="/tickets/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Creá tu primer ticket
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
